import React, { useEffect, useMemo, useState } from 'react';
import TableDragSelect from 'react-table-drag-select';
// import './App.css';
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Theme,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import moment from 'moment';
import 'react-table-drag-select/style.css';
import { tConvert } from '../modals/CadenceModal';

const useStyles = makeStyles<Theme, { showLessHours: boolean }>((theme) => ({
  '@global': {
    '.table-drag-select': {
      width: 'min-content',
    },
    '.table-drag-select td.cell-being-selected': {
      backgroundColor: '#3f83d5',
    },
    '.table-drag-select td.cell-selected.cell-being-selected': {
      backgroundColor: '#85a9d4',
    },
    '.table-drag-select td.cell-selected': {
      backgroundColor: '#81d481',
    },
  },
  amPm: {
    justifyContent: 'space-around',
    '& time': {
      flexGrow: 'initial',
    },
  },
  allHour: {
    border: 'medium none',
    overflow: 'initial !important',
  },
  wholeHour: ({ showLessHours }) => ({
    width: showLessHours ? 40 : 20,
  }),
  halfHour: ({ showLessHours }) => ({
    fontSize: 'x-small',
    width: showLessHours ? 40 : 20,
  }),
  hoursTable: {
    marginLeft: '-30px',
  },
  cellLabel: {
    width: 85,
  },
  cell: {
    '& td': {
      textAlign: 'left',
      border: '0.1rem solid transparent',
    },
  },
  preset: {
    marginTop: 58,
    // maxWidth: '93px'
    marginLeft: '-15px',
  },
  presetButton: {
    marginBottom: theme.spacing(1),
    marginLeft: theme.spacing(2),
    width: '100%',
  },
  presetDropdown: {
    '& .MuiSelect-selectMenu': {
      paddingRight: 0,
    },
  },
  hourRangeContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  hourRange: {
    position: 'absolute',
    backgroundColor: '#f9f9f9',
    paddingLeft: '10px',
    paddingRight: '10px',
    pointerEvents: 'none',
    // flexWrap: 'nowrap',
    opacity: '0.9',
  },
  /**
   * Hide extend horus when showLessHours is true
   * NOTE: Cannot be done with clsx since TableDragSelect rendering
   * @param props Contains showLessHours boolean
   */
  extendedHours: ({ showLessHours }) => ({
    display: showLessHours ? 'none' : '',
  }),
  flexContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '24px',
  },
}));

type TSchedulableHours = Record<
  string,
  { day: string; hours: { endTime: string; startTime: string }[] }
>;

const emptyHours: TSchedulableHours = {
  1: {
    day: 'Monday',
    hours: [],
  },
  2: {
    day: 'Tuesday',
    hours: [],
  },
  3: {
    day: 'Wednesday',
    hours: [],
  },
  4: {
    day: 'Thursday',
    hours: [],
  },
  5: {
    day: 'Friday',
    hours: [],
  },
  6: {
    day: 'Saturday',
    hours: [],
  },
  7: {
    day: 'Sunday',
    hours: [],
  },
};

const presetHours: Record<string, TSchedulableHours> = {
  MF96: {
    1: {
      day: 'Monday',
      hours: [{ startTime: '09:00', endTime: '18:00' }],
    },
    2: {
      day: 'Tuesday',
      hours: [{ startTime: '09:00', endTime: '18:00' }],
    },
    3: {
      day: 'Wednesday',
      hours: [{ startTime: '09:00', endTime: '18:00' }],
    },
    4: {
      day: 'Thursday',
      hours: [{ startTime: '09:00', endTime: '18:00' }],
    },
    5: {
      day: 'Friday',
      hours: [{ startTime: '09:00', endTime: '18:00' }],
    },
    6: {
      day: 'Saturday',
      hours: [],
    },
    7: {
      day: 'Sunday',
      hours: [],
    },
  },
  MF85: {
    1: {
      day: 'Monday',
      hours: [{ startTime: '08:00', endTime: '17:00' }],
    },
    2: {
      day: 'Tuesday',
      hours: [{ startTime: '08:00', endTime: '17:00' }],
    },
    3: {
      day: 'Wednesday',
      hours: [{ startTime: '08:00', endTime: '17:00' }],
    },
    4: {
      day: 'Thursday',
      hours: [{ startTime: '08:00', endTime: '17:00' }],
    },
    5: {
      day: 'Friday',
      hours: [{ startTime: '08:00', endTime: '17:00' }],
    },
    6: {
      day: 'Saturday',
      hours: [],
    },
    7: {
      day: 'Sunday',
      hours: [],
    },
  },
  MS24: {
    1: {
      day: 'Monday',
      hours: [{ startTime: '00:00', endTime: '23:59' }],
    },
    2: {
      day: 'Tuesday',
      hours: [{ startTime: '00:00', endTime: '23:59' }],
    },
    3: {
      day: 'Wednesday',
      hours: [{ startTime: '00:00', endTime: '23:59' }],
    },
    4: {
      day: 'Thursday',
      hours: [{ startTime: '00:00', endTime: '23:59' }],
    },
    5: {
      day: 'Friday',
      hours: [{ startTime: '00:00', endTime: '23:59' }],
    },
    6: {
      day: 'Saturday',
      hours: [{ startTime: '00:00', endTime: '23:59' }],
    },
    7: {
      day: 'Sunday',
      hours: [{ startTime: '00:00', endTime: '23:59' }],
    },
  },
};

const dayNames = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const tableLookup = Array.from({ length: 7 + 1 }, (i, dayIndex) =>
  Array.from({ length: 48 + 1 }, (j, timeIndex) => ({
    day: dayNames[dayIndex - 1] ?? '',
    time: {
      startTime: moment()
        .startOf('day')
        .add((timeIndex - 1) * 30, 'minutes')
        .format('HH:mm'),

      endTime: moment()
        .startOf('day')
        .add(timeIndex * 30 - (timeIndex === 48 ? 1 : 0), 'minutes') // last time will be 11:59 pm
        .format('HH:mm'),
    },
  }))
);

export function cellsToHours(cells: boolean[][]): TSchedulableHours {
  // cannot copy emptyHours since we are mutating hours
  const hours: TSchedulableHours = {
    1: {
      day: 'Monday',
      hours: [],
    },
    2: {
      day: 'Tuesday',
      hours: [],
    },
    3: {
      day: 'Wednesday',
      hours: [],
    },
    4: {
      day: 'Thursday',
      hours: [],
    },
    5: {
      day: 'Friday',
      hours: [],
    },
    6: {
      day: 'Saturday',
      hours: [],
    },
    7: {
      day: 'Sunday',
      hours: [],
    },
  };
  for (let dayIndex = 0; dayIndex < cells.length; dayIndex++) {
    // i refers to day of week
    for (let timeIndex = 0; timeIndex < cells[dayIndex].length; timeIndex++) {
      // is the element at that index true
      if (cells[dayIndex][timeIndex]) {
        // if so, is there a last element in the hours at day array and the last element's end is equal to the current start,
        if (
          hours[dayIndex].hours.slice(-1).pop()?.endTime ===
          tableLookup[dayIndex][timeIndex].time.startTime
        ) {
          (hours as any)[dayIndex].hours.slice(-1).pop().endTime =
            tableLookup[dayIndex][timeIndex].time.endTime;
          //  if so copy the current end into hours at that day end,
        } else {
          hours[dayIndex].hours.push({
            // creating shallow copy of object before pushing to array because modifying it in the array will cause the tableLookup object to modified as well
            ...tableLookup[dayIndex][timeIndex].time,
          });
        }
      }
    }
  }
  return hours;
}
export function hoursToCells(hours: TSchedulableHours): boolean[][] {
  const cells = Array.from({ length: 7 + 1 }, (i) =>
    Array.from({ length: 48 + 1 }, (j) => false)
  );
  // set variable to know when to stop setting cells to true
  let looping = false;
  //iterate over the tableLookups 8 arrays
  for (let i = 1; i < tableLookup.length; i++) {
    //iterate over nested arrays
    for (let j = 1; j < tableLookup[i].length; j++) {
      // if there is an array in the hours
      if (hours[i]) {
        //if looping is set to true then make the cells true
        if (looping) {
          cells[i][j] = true;
        }
        // iterate over array at the day in hours
        for (let k = 0; k < hours[i].hours.length; k++) {
          // if the current start time in the hours from formik matches the current day index and hour/minute index
          if (
            hours[i].hours[k].startTime === tableLookup[i][j].time.startTime
          ) {
            //then  set the corresponding cell index's to true and set looping to true
            cells[i][j] = true;
            looping = true;
          }
          //if the end dates match
          if (hours[i].hours[k].endTime === tableLookup[i][j].time.endTime) {
            //then set looping to false
            looping = false;
          }
        }
      }
    }
  }
  return cells;
}

export function createHoursString(hours: TSchedulableHours | undefined) {
  if (hours === undefined) return '';

  // console.log(hours, 'hours in create hours string ------');
  const dayHours = Object.keys(hours).map((day, index) => {
    return {
      startDay: hours[day].day,
      endDay: hours[day].day,
      hours: hours[day]?.hours
        ?.map(
          (timeRange) =>
            `${tConvert(timeRange.startTime)} to ${tConvert(timeRange.endTime)}`
        )
        ?.join(', '),
    };
  });
  // console.table(dayHours);
  let looping = true;
  let index = 1;
  while (looping) {
    if (dayHours[index]?.startDay === 'Sunday' || index === dayHours.length) {
      // console.log(index);
      looping = false;
    }
    if (dayHours[index]?.hours === dayHours[index - 1]?.hours) {
      if (dayHours[index]) {
        dayHours[index - 1].endDay = dayHours[index]?.startDay;
        dayHours.splice(index, 1);
      }
    } else {
      index += 1;
    }
  }
  let finalString = '';
  for (let i = 0; i < dayHours.length; i++) {
    if (dayHours[i].hours !== '') {
      if (dayHours[i].startDay === dayHours[i].endDay) {
        finalString += dayHours[i].startDay + ' ' + dayHours[i].hours + ', ';
      } else {
        finalString +=
          dayHours[i].startDay +
          ' to ' +
          dayHours[i].endDay +
          ' ' +
          dayHours[i].hours +
          ', ';
      }
    }
  }
  const final = finalString.slice(0, -2);
  return final;
}

export function calculateShowLessHours(hours: TSchedulableHours | undefined) {
  let shouldShowLessHours = true;
  const startTimeBound = '07:00';
  const endTimeBound = '18:30';
  if (hours === undefined) return '';
  Object.keys(hours).forEach((day, index) => {
    hours[day]?.hours?.forEach((timeRange) => {
      if (
        timeRange?.startTime < startTimeBound ||
        timeRange?.endTime > endTimeBound
      ) {
        shouldShowLessHours = false;
      }
    });
  });
  return shouldShowLessHours;
}

/**
 * UI for scheduling hours
 *
 * @param props
 */
export function Scheduler(props: {
  /** Incoming  hours to show to user */
  schedulableHours: TSchedulableHours;
  /** Callback to set derived hours from cells */
  setSchedulableHours: React.Dispatch<TSchedulableHours>;
}) {
  const [cells, setCells] = useState(hoursToCells(props.schedulableHours));

  const hours = useMemo(() => cellsToHours(cells), [cells]);

  useEffect(() => {
    props.setSchedulableHours(hours);
  }, [cells]);

  const handleResetCells = () => {
    setCells(hoursToCells(props.schedulableHours));
  };

  const handleClearCells = () => {
    setCells(hoursToCells(emptyHours));
  };

  const handlePresetCells = (
    event: SelectChangeEvent<any> | React.ChangeEvent<any>
  ) => {
    if (event.target.value) {
      setCells(hoursToCells(presetHours[event.target.value]));
    }
  };

  const shouldShowLessHours = !!calculateShowLessHours(hours);
  const [showLessHours, setShowLessHours] = useState(shouldShowLessHours);
  const classes = useStyles({ showLessHours });

  const handleShowMoreOrLessHours = () => {
    setShowLessHours((state) => !state);
  };

  // extract body to component - parameters = (businsessHoursMemo, cells, setCells, handleResetCells)
  return (
    <div className={clsx('container', classes.flexContainer)}>
      <header className={clsx('row', classes.amPm)}>
        <time className="col">AM</time>
        <time className="col">PM</time>
      </header>
      <main className="row">
        <div className={clsx('col', classes.hourRangeContainer)}>
          {Object.keys(hours).map((day, index) => (
            <div
              key={index}
              className={classes.hourRange}
              style={{
                top: 68 + 42 * index,
              }}
            >
              {hours[day].hours
                .map(
                  (timeRange) =>
                    `${tConvert(timeRange.startTime)} - ${tConvert(
                      timeRange.endTime
                    )}`
                )
                .join(', ')}
            </div>
          ))}
          <TableDragSelect
            className={clsx('table-drag-select', classes.hoursTable)}
            value={cells}
            onChange={(changedCells: boolean[][]) => setCells(changedCells)}
          >
            <tr>
              <td className={classes.cellLabel} {...{ disabled: true }} />
              {Array.from({ length: 48 }).map((_, i) => (
                <td
                  key={i}
                  className={clsx(classes.allHour, {
                    [classes.halfHour]: i % 2,
                    [classes.wholeHour]: !(i % 2),
                    [classes.extendedHours]: i < 14 || i > 37,
                  })}
                  {...{ disabled: true }}
                >
                  {i % 2 ? 30 : Math.floor(i / 2) % 12 || 12}
                </td>
              ))}
            </tr>
            {dayNames.map((day) => (
              <tr key={day} className={classes.cell}>
                <td className={classes.cellLabel} {...{ disabled: true }}>
                  {day}
                </td>
                {Array.from({ length: 48 }).map((_, i) => (
                  <td
                    data-qa={`${day.toLocaleLowerCase()}-cell-${i}`}
                    key={i}
                    className={clsx({
                      [classes.extendedHours]: i < 14 || i > 37,
                    })}
                  />
                ))}
              </tr>
            ))}
          </TableDragSelect>
          <aside className={classes.preset}>
            <Button
              className={classes.presetButton}
              color="primary"
              variant="contained"
              disableElevation={true}
              onClick={handleClearCells}
              data-qa="clear-powerlist-hours"
            >
              Clear
            </Button>
            <Button
              className={classes.presetButton}
              color="primary"
              variant="contained"
              disableElevation={true}
              onClick={handleResetCells}
              data-qa="restore-powerlist-hours"
            >
              Restore
            </Button>
            <Button
              className={classes.presetButton}
              color="primary"
              variant="contained"
              disableElevation={true}
              onClick={handleShowMoreOrLessHours}
              data-pup="show-less-hours"
            >
              {showLessHours ? 'Show More Hours' : 'Show Less Hours'}
            </Button>
            <FormControl
              className={clsx(classes.presetButton, classes.presetDropdown)}
              variant="outlined"
              fullWidth
              size="small"
            >
              <InputLabel id="presets-label">Presets</InputLabel>
              <Select
                labelId="preset-label"
                id="presets-outlined"
                label="Presets"
                defaultValue=""
                name="presetHours"
                onChange={handlePresetCells}
                data-pup="powerlist-hours-presets"
              >
                <MenuItem value="">
                  <em data-qa="select-a-range-businesss-hours">
                    Select a Range
                  </em>
                </MenuItem>
                <MenuItem data-qa="mf-9-5" value="MF96">
                  MF 9AM-6PM
                </MenuItem>
                <MenuItem data-qa="mf-8-5" value="MF85">
                  MF 8AM-5PM
                </MenuItem>
                <MenuItem data-qa="24/7" value="MS24">
                  24 HOURS
                </MenuItem>
              </Select>
            </FormControl>
          </aside>
        </div>
      </main>
    </div>
  );
}
